import type { AiCostBreakdownRowPayload } from '@Application/AiCosts/Actions/aiCostBreakdownFetched';
import type PlainDate from '@Domain/PlainDate';
import AiCostAudioView from '@UI/Store/AiCosts/Views/AiCostAudioView';
import AiCostDocumentsView from '@UI/Store/AiCosts/Views/AiCostDocumentsView';
import AiCostQuantitiesView from '@UI/Store/AiCosts/Views/AiCostQuantitiesView';
import toPlainDate from '@UI/Store/AiCosts/Views/toPlainDate';

type Reference = { readonly id: number; readonly label: string | null };

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * One aggregated line of a breakdown. `subjectId` is `null` for a cost that cannot be attributed;
 * `subjectLabel` is `null` for a deleted subject and always for an interview. Each reference
 * (`organization`, `facility`, `user`) has a `null` label once deleted and is only sent for the axes
 * that need it. `callCount`, `unpricedCallCount` and `quantities` are `null` outside the
 * super-administrator's view.
 */
class AiCostBreakdownRowView {
	readonly subjectId: number | null;
	readonly subjectLabel: string | null;
	readonly subjectDate: PlainDate | null;
	readonly isSubjectDeleted: boolean;
	readonly interviewKind: 'conversation' | 'notes' | null;
	readonly usageLabel: string | null;
	readonly organization: Reference | null;
	readonly facility: Reference | null;
	readonly facilityCount: number | null;
	readonly user: Reference | null;
	readonly processedAt: Date | null;
	readonly audio: AiCostAudioView;
	readonly documents: AiCostDocumentsView;
	readonly callCount: number | null;
	readonly costAmount: number;
	readonly unpricedCallCount: number | null;
	readonly quantities: AiCostQuantitiesView | null;

	constructor(payload: AiCostBreakdownRowPayload) {
		this.subjectId = payload.subject.id;
		this.subjectLabel = payload.subject.label;
		this.subjectDate = toPlainDate(payload.subject.date);
		this.isSubjectDeleted = payload.subject.isDeleted;
		this.interviewKind = payload.interviewKind === null ? null : payload.interviewKind.value;
		this.usageLabel = payload.usage === null ? null : payload.usage.label;
		this.organization = payload.organization;
		this.facility = payload.facility;
		this.facilityCount = payload.facilityCount;
		this.user = payload.user;
		this.processedAt = payload.processedAt === null ? null : new Date(payload.processedAt);
		this.audio = new AiCostAudioView(payload.audio);
		this.documents = new AiCostDocumentsView(payload.documents);
		this.callCount = payload.callCount;
		this.costAmount = payload.cost.amount;
		this.unpricedCallCount = payload.cost.unpricedCallCount;
		this.quantities = payload.quantities === null ? null : new AiCostQuantitiesView(payload.quantities);

		Object.freeze(this);
	}

	get hasQuantities(): boolean {
		return this.quantities !== null;
	}

	get unpricedItemCount(): number {
		return this.audio.unpricedCount + this.documents.unpricedCount;
	}
}

export default AiCostBreakdownRowView;
